<template>
  <div class="salon-app salon-service">
    <!-- Top app bar + booking progress -->
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.service.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <span class="salon-progress-label">{{ $t("salon.service.stepOf", { step, total: totalSteps }) }}</span>
      </div>
    </header>

    <main class="salon-main">
      <section class="salon-intro">
        <h2 class="salon-intro-title">{{ $t("salon.service.heading") }}</h2>
        <p v-if="branchName" class="salon-intro-outlet">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M12 21s7-6.2 7-11a7 7 0 10-14 0c0 4.8 7 11 7 11z" fill="none"
              stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
            <circle cx="12" cy="10" r="2.5" fill="none" stroke="currentColor" stroke-width="1.6" />
          </svg>
          <span>{{ branchName }}</span>
        </p>
      </section>

      <!-- Category tabs. "All" is not a category the merchant configured, it is the
           unfiltered view; every other tab is a display category of the outlet's menu set. -->
      <div v-if="categories.length" class="salon-tabs" role="tablist" :aria-label="$t('salon.service.categoriesLabel')">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="salon-tab"
          :class="{ 'salon-tab--active': tab.id === activeCategoryId }"
          type="button"
          role="tab"
          :aria-selected="String(tab.id === activeCategoryId)"
          @click="activeCategoryId = tab.id"
        >
          {{ tab.name }}
        </button>
      </div>

      <div class="salon-services">
        <article
          v-for="service in visibleServices"
          :key="service.key"
          class="salon-service-card"
          :class="{
            'salon-service-card--selected': isSelected(service),
            'salon-service-card--unavailable': !service.is_available,
          }"
        >
          <div
            class="salon-service-body"
            role="button"
            tabindex="0"
            :aria-label="$t('salon.service.viewService', { name: service.name })"
            @click="openSheet(service)"
            @keydown.enter.prevent="openSheet(service)"
            @keydown.space.prevent="openSheet(service)"
          >
            <h3 class="salon-service-name">{{ service.name }}</h3>
            <p v-if="service.description" class="salon-service-desc">{{ service.description }}</p>
            <div class="salon-service-meta">
              <span v-if="displayDuration(service)" class="salon-service-duration">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2" />
                  <path d="M12 7v5l3 2" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                {{ $t("salon.service.minutes", { n: displayDuration(service) }) }}
              </span>
              <!-- SH032 — the outlet decides whether a price is quoted here at all. Off,
                   the card keeps its duration and the customer is quoted at the chair. -->
              <span v-if="showPrice" class="salon-service-price">
                <span v-if="service.price_from && !isConfigured(service)" class="salon-service-from">{{ $t("salon.service.from") + " " }}</span>{{ displayPrice(service) }}
              </span>
            </div>
          </div>

          <div class="salon-service-side">
            <div class="salon-service-media">
              <!-- Items with no photo come back pointing at the API's noimage placeholder,
                   which is not always served; a broken-image glyph reads worse than the
                   empty tile, so drop the img and leave the tile. -->
              <img
                v-if="service.image && !brokenImages.includes(service.id)"
                :src="service.image"
                :alt="service.name"
                @error="onImageError(service)"
              />
            </div>
            <button
              class="salon-service-toggle"
              :class="{ 'salon-service-toggle--on': isSelected(service) }"
              type="button"
              :disabled="!service.is_available"
              :aria-pressed="String(isSelected(service))"
              :aria-label="$t(isSelected(service) ? 'salon.service.removeService' : 'salon.service.addService', { name: service.name })"
              @click="toggle(service)"
            >
              <svg v-if="isSelected(service)" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </article>

        <p v-if="!isLoading && loadFailed" class="salon-empty salon-empty--error">
          {{ $t("salon.service.loadError") }}
          <button class="salon-retry" type="button" @click="getServices">{{ $t("salon.service.retry") }}</button>
        </p>
        <p v-else-if="!isLoading && !visibleServices.length" class="salon-empty">
          {{ $t("salon.service.empty") }}
        </p>
      </div>
    </main>

    <nav class="salon-cta">
      <span class="salon-cta-count">{{ selectionLabel }}</span>
      <button class="salon-cta-btn" type="button" :disabled="!selectedIds.length" @click="goToNextStep">
        {{ $t("salon.service.next") }}
      </button>
    </nav>

    <SalonServiceDetailSheet
      v-if="sheetService"
      :key="sheetService.id"
      :service="sheetService"
      :branch-code="branchCode"
      :menuset-id="sheetService.menuset_id"
      :show-price="showPrice"
      @close="closeSheet"
      @add="onSheetAdd"
    />

    <material-loading :active="isLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonServiceDetailSheet from "@/salon/components/ServiceDetailSheet.vue";
import OrderService from "@/services/order.service";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

// SH004 is the fourth of the nine salon booking steps (Salon_UI/select_service).
const STEP = 4;
const TOTAL_STEPS = 9;

// The "All" tab is synthetic — no display category can have id 0.
const ALL_CATEGORIES = 0;

export default {
  name: "SalonSelectService",
  components: {
    SalonProfileMenu,
    MaterialLoading,
    SalonServiceDetailSheet,
  },
  props: {
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    branchCode: {
      type: String,
      default: "",
    },
    branchName: {
      type: String,
      default: "",
    },
    // SH032 — minty_branches.salon_show_service_price, carried down from the outlet the
    // customer picked at step 1. False keeps every price off this screen.
    showPrice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["back", "selected", "profile"],
  data() {
    return {
      step: STEP,
      totalSteps: TOTAL_STEPS,
      isLoading: false,
      categories: [],
      loadFailed: false,
      brokenImages: [],
      activeCategoryId: ALL_CATEGORIES,
      selectedIds: [],
      // SH005 — the service whose detail sheet is open, and what the chosen service
      // was configured with (add-ons, note). Keyed by item id.
      sheetService: null,
      configs: {},
      // Next opens the detail sheet for the chosen service; this marks that sheet as
      // step 5 of the flow rather than a look at a card, so its Add ends step 4.
      isWalking: false,
    };
  },
  computed: {
    progressWidth() {
      return ((STEP / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },

    tabs() {
      return [
        { id: ALL_CATEGORIES, name: this.$t("salon.service.all") },
        ...this.categories.map((category) => ({
          id: category.id,
          name: category.second_name || category.name || "",
        })),
      ];
    },

    // One flat list per category. A menu set nests items under a category directly and
    // again under its groups and sub groups; the salon picker has no such hierarchy, so
    // all three levels collapse into the category's own list.
    servicesByCategory() {
      return this.categories.map((category) => ({
        id: category.id,
        services: this.collectItems(category).map((item) => this.toService(category, item)),
      }));
    },

    visibleServices() {
      const groups =
        this.activeCategoryId === ALL_CATEGORIES
          ? this.servicesByCategory
          : this.servicesByCategory.filter((group) => group.id === this.activeCategoryId);

      // The same item can sit in more than one category, so "All" would list it twice.
      const seen = new Set();

      return groups
        .flatMap((group) => group.services)
        .filter((service) => {
          if (seen.has(service.id)) {
            return false;
          }
          seen.add(service.id);
          return true;
        });
    },

    // SH021 — one service per booking, so the bar names it instead of counting it.
    selectionLabel() {
      const [service] = this.chosenServices();

      return service ? service.name : this.$t("salon.service.noneSelected");
    },
  },
  created() {
    this.getServices();
  },
  methods: {
    async getServices() {
      if (!this.branchCode) {
        return;
      }

      this.isLoading = true;
      this.loadFailed = false;

      try {
        // menuset_id is ignored by the endpoint — it re-reads the outlet's own menu set
        // from branch_code — but the parameter is part of the contract, so send it.
        const response = await OrderService.getListCategory({
          menuset_id: 0,
          branch_code: this.branchCode,
        });

        if (response.data.result.isSuccess) {
          this.categories = response.data.data.categories ?? [];
        } else {
          this.categories = [];
          this.loadFailed = true;
        }
      } catch (error) {
        // A swallowed failure here is indistinguishable from an outlet with nothing to
        // book, and that hid a missing tenant column for a whole round of testing.
        this.categories = [];
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    collectItems(category) {
      const items = [...(category.items ?? [])];

      (category.groups ?? []).forEach((group) => {
        items.push(...(group.items ?? []));

        (group.sub_groups ?? []).forEach((subGroup) => {
          items.push(...(subGroup.sub_group_items ?? subGroup.items ?? []));
        });
      });

      return items;
    },

    toService(category, item) {
      // Everyone reaching this step signed in at step 2 or 3, so the member price is the
      // price they will pay. The API already falls it back to the default price when the
      // outlet has no member pricing.
      const isRange = Boolean(item.choice || item.is_sub_category);
      const price = isRange && item.min_choice_member_price != null
        ? item.min_choice_member_price
        : item.member_price;

      return {
        key: category.id + "-" + item.id,
        id: item.id,
        category_id: category.id,
        menuset_id: category.set_id ?? 0,
        name: item.item_name || "",
        description: item.description || "",
        image: item.image || "",
        duration_min: Number(item.service_duration_min || 0),
        price: Number(price || 0),
        price_from: isRange,
        price_text: this.formatPrice(price),
        is_available: this.isAvailable(item),
      };
    },

    // Same rule the MOO menu uses: a countdown item needs stock left, everything else
    // only has to be flagged available.
    isAvailable(item) {
      if (item.stock_enable_balance) {
        return item.stock_balance > 0;
      }

      return item.is_not_available == 0;
    },

    formatPrice(price) {
      return "$" + parseFloat(price || 0).toFixed(2);
    },

    // SH005 — the card body opens the detail sheet; the round toggle beside it stays a
    // quick add for a customer who wants the service exactly as listed.
    openSheet(service) {
      if (!service.is_available) {
        return;
      }

      this.sheetService = { ...service, ...(this.configs[service.id] || {}) };
    },

    closeSheet() {
      // Backing out of the sheet leaves the customer on the menu with the service still
      // chosen, so Next can reopen it.
      this.isWalking = false;
      this.sheetService = null;
    },

    // SH021 — adding from the sheet replaces whatever was chosen before, its add-ons
    // and note included; a booking carries exactly one service.
    onSheetAdd(payload) {
      this.configs = {
        [payload.service_id]: {
          selection: payload.selection,
          add_ons: payload.add_ons,
          note: payload.note,
          total_price: payload.total_price,
          // SH023 — what the sheet totalled, so the card can print it without re-adding
          // the add-ons itself.
          total_duration_min: payload.total_duration_min,
        },
      };
      this.selectedIds = [payload.service_id];
      this.sheetService = null;

      // The sheet was opened by Next, so step 4 is done.
      if (this.isWalking) {
        this.isWalking = false;
        this.emitSelection();
      }
    },

    onImageError(service) {
      if (!this.brokenImages.includes(service.id)) {
        this.brokenImages.push(service.id);
      }
    },

    isSelected(service) {
      return this.selectedIds.includes(service.id);
    },

    // SH023 — a service is as long as its add-ons make it. Once the sheet has totalled
    // them the card has to say so, or the customer reads 45 min here and meets 65 min on
    // the date step with nothing in between to explain the jump. A service added straight
    // from the round toggle has no configuration, so it falls back to its own duration.
    displayDuration(service) {
      const config = this.configs[service.id];

      if (config && config.total_duration_min != null) {
        return Number(config.total_duration_min);
      }

      return Number(service.duration_min || 0);
    },

    // The minutes and the price are one statement about the same service, so the price
    // has to move with them. A configured service also has an exact price rather than a
    // range, so "from" comes off with it.
    displayPrice(service) {
      const config = this.configs[service.id];

      if (config && config.total_price != null) {
        return this.formatPrice(config.total_price);
      }

      return service.price_text;
    },

    isConfigured(service) {
      const config = this.configs[service.id];

      return Boolean(config && config.total_price != null);
    },

    // SH021 — one service at a time: picking another replaces the current one, and
    // tapping the chosen service again clears it. Either way the configuration goes
    // with it, or re-adding a service later silently brings back add-ons and a note the
    // customer thought were gone.
    toggle(service) {
      if (!service.is_available) {
        return;
      }

      this.selectedIds = this.isSelected(service) ? [] : [service.id];
      this.configs = {};
    },

    // Read from every category, not just the visible tab: a service chosen under
    // "Hair Cut" must still travel when the customer moves to the "Colour" tab. The
    // result holds at most one service.
    chosenServices() {
      return this.servicesByCategory
        .flatMap((group) => group.services)
        .filter((service) => this.isSelected(service))
        .filter((service, index, list) => list.findIndex((s) => s.id === service.id) === index);
    },

    emitSelection() {
      const services = this.chosenServices().map((service) => ({
        ...service,
        ...(this.configs[service.id] || {}),
      }));

      this.$emit("selected", { service_ids: [...this.selectedIds], services });
    },

    // The detail sheet is step 5, sitting between this list and the date picker, so
    // Next opens it for the chosen service.
    goToNextStep() {
      const [service] = this.chosenServices();

      if (!service) {
        return;
      }

      this.isWalking = true;
      this.openSheet(service);
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-service {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--salon-primary);
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-primary);
}

.salon-progress {
  margin-top: 16px;
  text-align: center;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-surface-container-highest);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary-container);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.salon-progress-label {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: var(--salon-stack-md) 0 120px;
}

.salon-intro {
  padding: 0 var(--salon-container-padding);
  margin-bottom: var(--salon-stack-lg);
}

.salon-intro-title {
  margin: 0 0 4px;
  font-family: var(--salon-font-headline);
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.01em;
  color: var(--salon-primary);
}

.salon-intro-outlet {
  display: flex;
  gap: 4px;
  align-items: center;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Category tabs ─────────────────────────────────────────────── */
.salon-tabs {
  display: flex;
  gap: 8px;
  padding: 0 16px 8px var(--salon-container-padding);
  margin-bottom: var(--salon-stack-lg);
  overflow-x: auto;
  white-space: nowrap;
  /* The row scrolls sideways; a visible bar under the pills reads as a stray divider. */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.salon-tabs::-webkit-scrollbar {
  display: none;
}

.salon-tab {
  flex-shrink: 0;
  padding: 8px 20px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface);
  border: 1px solid var(--salon-surface-variant);
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.salon-tab:hover:not(.salon-tab--active) {
  background: var(--salon-surface-variant);
}

.salon-tab--active {
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border-color: var(--salon-primary);
  box-shadow: 0 2px 6px rgba(28, 28, 30, 0.16);
}

/* ── Service cards ─────────────────────────────────────────────── */
.salon-services {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
  padding: 0 var(--salon-container-padding);
}

.salon-service-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px;
  background: var(--salon-surface-lowest);
  border: 2px solid transparent;
  border-radius: 12px;
  box-shadow: var(--salon-shadow-soft);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.salon-service-card--selected {
  border-color: var(--salon-gold);
  box-shadow: 0 0 15px rgba(201, 162, 39, 0.3);
}

.salon-service-card--unavailable {
  opacity: 0.5;
}

.salon-service-body {
  flex: 1;
  min-width: 0;
  padding-right: 16px;
  cursor: pointer;
}

.salon-service-card--unavailable .salon-service-body {
  cursor: default;
}

.salon-service-name {
  margin: 0 0 4px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-primary);
}

.salon-service-desc {
  display: -webkit-box;
  margin: 0 0 8px;
  overflow: hidden;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.salon-service-meta {
  display: flex;
  gap: 12px;
  align-items: center;
}

.salon-service-duration {
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-service-price {
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-primary);
}

.salon-service-card--selected .salon-service-price {
  color: var(--salon-gold);
}

.salon-service-from {
  font-weight: 400;
}

.salon-service-side {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
  align-items: center;
}

.salon-service-media {
  width: 72px;
  height: 72px;
  overflow: hidden;
  background: var(--salon-surface-variant);
  border-radius: 8px;
}

.salon-service-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.salon-service-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--salon-primary);
  background: var(--salon-surface);
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.salon-service-toggle:hover:not(:disabled) {
  color: var(--salon-on-primary);
  background: var(--salon-primary);
}

.salon-service-toggle--on {
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border-color: var(--salon-primary);
}

.salon-service-toggle:disabled {
  cursor: not-allowed;
}

.salon-empty {
  padding: 24px 0;
  margin: 0;
  font-size: 14px;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

.salon-empty--error {
  color: var(--salon-error);
}

.salon-retry {
  display: block;
  padding: 8px 20px;
  margin: 12px auto 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-count {
  overflow: hidden;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salon-cta-btn {
  flex-shrink: 0;
  padding: 12px 32px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
